import React from 'react';
import { Search, Target, Radar, Shield, Crosshair, Eye } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';

export function ThreatHunting() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Détection Proactive des Menaces',
        en: 'Proactive Threat Hunting'
      },
      subtitle: {
        fr: 'Identification et neutralisation des menaces avant qu\'elles ne deviennent actives',
        en: 'Identify and neutralize threats before they become active'
      }
    },
    features: [
      {
        icon: <Target className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Détection Avancée',
          en: 'Advanced Detection'
        },
        description: {
          fr: 'Utilisation de techniques avancées pour identifier les menaces',
          en: 'Using advanced techniques to identify threats'
        }
      },
      {
        icon: <Radar className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Surveillance Continue',
          en: 'Continuous Monitoring'
        },
        description: {
          fr: 'Analyse en temps réel de votre environnement',
          en: 'Real-time analysis of your environment'
        }
      },
      {
        icon: <Eye className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Visibilité Totale',
          en: 'Complete Visibility'
        },
        description: {
          fr: 'Vue complète sur votre infrastructure',
          en: 'Complete view of your infrastructure'
        }
      }
    ],
    methodology: {
      title: {
        fr: 'Notre Méthodologie',
        en: 'Our Methodology'
      },
      steps: [
        {
          icon: <Search className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Hypothèse',
            en: 'Hypothesis'
          },
          description: {
            fr: 'Développement de scénarios de menaces potentielles',
            en: 'Development of potential threat scenarios'
          }
        },
        {
          icon: <Target className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Investigation',
            en: 'Investigation'
          },
          description: {
            fr: 'Recherche approfondie basée sur les hypothèses',
            en: 'In-depth research based on hypotheses'
          }
        },
        {
          icon: <Shield className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Identification',
            en: 'Identification'
          },
          description: {
            fr: 'Découverte et confirmation des menaces',
            en: 'Discovery and confirmation of threats'
          }
        },
        {
          icon: <Crosshair className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Neutralisation',
            en: 'Neutralization'
          },
          description: {
            fr: 'Élimination des menaces identifiées',
            en: 'Elimination of identified threats'
          }
        }
      ]
    },
    capabilities: {
      title: {
        fr: 'Nos Capacités',
        en: 'Our Capabilities'
      },
      items: [
        {
          title: {
            fr: 'Intelligence des Menaces',
            en: 'Threat Intelligence'
          },
          description: {
            fr: 'Utilisation des dernières données sur les menaces',
            en: 'Leveraging the latest threat data'
          }
        },
        {
          title: {
            fr: 'Analyse Comportementale',
            en: 'Behavioral Analysis'
          },
          description: {
            fr: 'Détection des comportements suspects',
            en: 'Detection of suspicious behaviors'
          }
        },
        {
          title: {
            fr: 'Machine Learning',
            en: 'Machine Learning'
          },
          description: {
            fr: 'Algorithmes avancés pour la détection des menaces',
            en: 'Advanced algorithms for threat detection'
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Search className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Threat Hunting Visualization */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80" 
              alt="Threat Detection Center"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Centre de Détection' : 'Detection Center'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Surveillance 24/7 des menaces avec analyse en temps réel'
                  : '24/7 threat monitoring with real-time analysis'}
              </p>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&q=80" 
              alt="Advanced Analytics"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Analyse Avancée' : 'Advanced Analytics'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Utilisation de l\'IA pour détecter les menaces sophistiquées'
                  : 'Using AI to detect sophisticated threats'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Methodology Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.methodology.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 inset-y-0 w-0.5 bg-blue-500/30"></div>
              <div className="space-y-12">
                {content.methodology.steps.map((step, index) => (
                  <div key={index} className="relative flex items-center gap-8">
                    <div className="absolute left-8 w-3 h-3 bg-blue-500 rounded-full transform -translate-x-1.5"></div>
                    <div className="pl-20">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {step.title[language]}
                      </h3>
                      <p className="text-gray-300">
                        {step.description[language]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Visual Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="relative rounded-2xl overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80"
            alt="Security Operations"
            className="w-full h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/50 flex items-center">
            <div className="p-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                {language === 'fr' ? 'Protection Proactive' : 'Proactive Protection'}
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl">
                {language === 'fr'
                  ? 'Notre équipe d\'experts utilise des technologies de pointe pour identifier et neutraliser les menaces avant qu\'elles ne causent des dommages.'
                  : 'Our team of experts uses cutting-edge technology to identify and neutralize threats before they cause damage.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Capabilities Section */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          {content.capabilities.title[language]}
        </h2>
        <div className="max-w-4xl mx-auto space-y-8">
          {content.capabilities.items.map((item, index) => (
            <div key={index} className="flex items-start space-x-4 bg-gray-800/50 p-6 rounded-lg">
              <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                <Shield className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {item.title[language]}
                </h3>
                <p className="text-gray-300">
                  {item.description[language]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Renforcez Votre Sécurité' : 'Strengthen Your Security'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Découvrez comment notre approche proactive peut protéger votre entreprise' 
                : 'Discover how our proactive approach can protect your business'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Commencer Maintenant' : 'Get Started Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}